import { Injectable } from '@angular/core';
import { FirestoreService } from '../firebase/firestore.service';
import { Note } from './store/notes.model';
import { Update } from '@ngrx/entity';
import { map } from 'rxjs/operators';
import { FirestoreDocumentChangeType } from '../firebase/firebase.model';
import {
    ActionAddNoteToStore,
    ActionUpdateNoteInStore,
    ActionRemoveNoteFromStore
} from './store/notes.actions';
import { Logger } from '../logging/logger';

@Injectable({ providedIn: 'root' })
export class NotesDataService {
    constructor(private firestoreService: FirestoreService, private logger: Logger) {}

    async createNote(note: Partial<Note>): Promise<Partial<Note>> {
        const notesCollection = this.firestoreService.collection('notes');
        const noteDoc = await this.firestoreService.addDoc(notesCollection, note);
        return {
            ...note,
            id: noteDoc.id
        };
    }

    fetchNotes(classId: string, createdBy: string) {
        const q = this.firestoreService.query(
            this.firestoreService.collection('notes'),
            this.firestoreService.where('classId', '==', classId),
            this.firestoreService.where('createdBy', '==', createdBy),
            this.firestoreService.where('deleted', '==', false)
        );
        return this.firestoreService.stateChanges(q).pipe(
            map(changes => {
                this.logger.debug('firebase changes: ', changes);
                const actionsToDispatch = [];
                changes.forEach(change => {
                    const data = change.doc.data() as Note;
                    const id = change.doc.id;
                    switch (change.type) {
                        case FirestoreDocumentChangeType.added:
                            actionsToDispatch.push(new ActionAddNoteToStore({ note: { ...data, id } }));
                            break;
                        case FirestoreDocumentChangeType.modified:
                            actionsToDispatch.push(new ActionUpdateNoteInStore({ note: { ...data, id } }));
                            break;
                        case FirestoreDocumentChangeType.removed:
                            actionsToDispatch.push(new ActionRemoveNoteFromStore({ note: { ...data, id } }));
                            break;
                    }
                });
                return actionsToDispatch;
            })
        );
    }

    updateNote(update: Update<Note>): Promise<void> {
        const noteRef = this.firestoreService.doc('notes', update.id as string);
        return this.firestoreService.updateDoc(noteRef, {
            ...update.changes
        });
    }

    removeNotes(ids: string[]): Promise<void[]> {
        const updates = ids.map(id => {
            const update: Update<Note> = {
                id,
                changes: {
                    deleted: true
                }
            };
            return this.updateNote(update);
        });
        return Promise.all(updates);
    }
}
